import { add, format, isEqual} from 'date-fns';
import { isUndefined } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Container } from 'components/Container';
import { DocumentTitle } from 'components/DocumentTitle';
import { useTrack } from 'utils/hooks/useMixPanel';
import { Loader } from 'components/Loader';
import { ScheduleMixpanelEvents } from 'domains/direct-schedule/mixpanel';
import { TimeSlotPicker } from 'components/TimeSlotPicker/TimeSlotPicker';
import { useDisplayTimezone } from 'domains/timezone/useDisplayTimezone';
import { AppointmentDate } from 'components/AppointmentDate';
import { Button } from 'components/Button';
import { Card } from 'components/Card';
import { Modal } from 'components/Modal';
import { useModal } from 'utils/hooks/useModal';
import config from 'config';
import { Heading, Paragraph } from 'components/Typography';
import { useScheduleState } from 'domains/direct-schedule/useScheduleProvider';
import { DayPicker } from 'components/DayPicker/DayPicker';

type ScheduleProps = {
  loading?: boolean;
  isError?: boolean;
};

/** Maximum days in the future or in the past an appointment can be scheduled */
export const MAX_SCHEDULE_DAYS_RANGE = 14;

export const getMaxScheduleDate = (appointmentDate: Date): Date => {
  return add(appointmentDate, { days: MAX_SCHEDULE_DAYS_RANGE });
};

const ScheduleUI = ({ loading = false, isError }: ScheduleProps) => {
  useTrack(ScheduleMixpanelEvents.ViewedSchedule);

  const { t } = useTranslation();
  const { openModal: openErrorModal, modalProps: errorModalProps } = useModal({ dismissable: false });
  const { openModal: openAppointmentNotFoundModal, modalProps: appointmentNotFoundModalProps } = useModal({ dismissable: true });
  const navigator = useNavigate();
  const { displayTimezone } = useDisplayTimezone();
  const {
    selectedDayIndex,
    setSelectedDayIndex,
    selectedTime,
    setSelectedTime,
    clientId,
    scheduleId,
    availableTimes,
    setAvailableTimes,
    scheduleInformation,
    scheduleQuery,
    setCalendarId,
    setTherapistName,
    isLoadingSelectedDay,
  } = useScheduleState();

  const availableTimesForDay = useMemo(() => {
    if (isUndefined(selectedDayIndex)) return [];
    return availableTimes[selectedDayIndex].availableTimes
  }, [isLoadingSelectedDay, selectedDayIndex]);

  const onChangeDay = useCallback(
    (newDayIndex: number) => {
      setSelectedDayIndex(newDayIndex);
      setSelectedTime(undefined);
    },
    [setSelectedDayIndex, setSelectedTime],
  );

  const onScroll = () => {
    const lastItem = availableTimes.at(-1);
    if (isUndefined(lastItem)) return;
    setAvailableTimes([...availableTimes, {
      day: add(lastItem.day, { days: 1 }),
      availableTimes: []
    }]);
  };

  const onConfirm = () => {
    if (isUndefined(selectedDayIndex) || isUndefined(selectedTime)) return;
    const { calendarID, therapistName } = availableTimes[selectedDayIndex].availableTimes.find(item => isEqual(item.time, selectedTime)) || {};
    if (isUndefined(calendarID) || isUndefined(therapistName)) return;
    setCalendarId(calendarID);
    setTherapistName(therapistName);
    navigator(`/schedule/${clientId}/${scheduleId}/confirm`);
  };

  useEffect(() => {
    if (isError && scheduleQuery.isFetched) openErrorModal();
  }, [isError, openErrorModal, scheduleQuery.isFetched]);

  useEffect(() => {
    if (selectedDayIndex === undefined) setSelectedDayIndex(0);
  }, []);

  return (
    <>
      <DocumentTitle>{t('schedule.pageTitle')}</DocumentTitle>
      <Container className="bg-cream-50 min-h-full max-w-[40rem] md:px-0 flex flex-col items-center" size="full">
        {loading && (
          <Loader
            data-testid="billing-loader"
            className="flex grow items-center justify-center p-24 text-primary-500"
          />
        )}
        {!loading && !isError && (
          <div>
            <Heading level="h2" className="mb-16">
              {t('schedule.mainHeading')}
            </Heading>

            <Paragraph className="mb-64">
              {t('schedule.subHeading', {
                timezone: displayTimezone?.shortName,
              })}
            </Paragraph>

            {displayTimezone && (
              <>
                <DayPicker
                  onChange={onChangeDay}
                  onInfiniteScroll={onScroll}
                  availableTimes={availableTimes.map(item => ({ ...item, availableTimes: item.availableTimes.map(time => time.time) }))}
                  disabledWhenNoAvailableTimes={false}
                  selectedDayIndex={selectedDayIndex}
                  maxDay={
                    scheduleInformation &&
                    getMaxScheduleDate(add (new Date(), { days: 3 }))
                  }
                  labelKey="schedule.availableDaysLabel"
                />
                
                {!isLoadingSelectedDay && availableTimesForDay.length > 0 && (
                  <TimeSlotPicker
                    onChange={setSelectedTime}
                    timeSlots={availableTimesForDay.map(time => time.time)}
                    value={selectedTime}
                    labelKey="schedule.availableTimesLabel"
                  />
                )}

                {isLoadingSelectedDay && (
                  <div className="-mt-40 h-96 flex items-center justify-center flex-1 gap-8 text-black-38%">
                    <Loader />
                    <span className="text-sm font-semibold">{t('schedule.confirm.loadingTimes')}</span>
                  </div>
                )}

                {!isLoadingSelectedDay && availableTimesForDay.length === 0 && selectedDayIndex !== undefined &&
                  <div className="-mt-40 h-96 w-full flex items-center justify-center">
                    <span className="text-black font-semibold text-sm tb-64">
                      {t('schedule.confirm.noAvailableTimes', { date: format(availableTimes[selectedDayIndex].day, 'EEEE, MMMM d') })}
                    </span>
                  </div>
                }
              </>
            )}

            {selectedTime && (
              <Card className='mb-48'>
                <Heading level="h6" className="mb-16">
                  {t('schedule.confirm.dateTimeTitle')}
                </Heading>
                <Heading level="h5" className="mt-40 mb-16">
                  {t('schedule.confirm.dateTimeSubTitle')}
                </Heading>
                <AppointmentDate date={selectedTime} />
              </Card>
            )}

            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex flex-col md:flex-row w-full md:w-auto gap-y-[24px] md:gap-x-16">
                <Button
                  variant="primary"
                  onClick={onConfirm}
                  size="lg"
                  disabled={isUndefined(selectedTime) || isUndefined(selectedDayIndex)}
                >
                  {t('schedule.submit')}
                </Button>
              </div>
              <Button size="lg" variant="ghost" onClick={openAppointmentNotFoundModal}>
                {t('schedule.cantFindAppointment')}
              </Button>
            </div>
          </div>
        )}
        <Modal {...errorModalProps}>
          <Heading level="h4" className="mb-16">
            {scheduleInformation?.status && scheduleInformation.status !== 'active'
              ? t('schedule.confirm.scheduleLinkExpired')
              : t('schedule.confirm.appointmentNoLongerValidTitle')}
          </Heading>
          <Heading level="h7">
            {scheduleInformation?.status && scheduleInformation.status !== 'active'
              ? 't'
              : t('schedule.cantFindAppointment.modal.expiredLink', {
                  supportEmail: config.get('supportEmail'),
                })}
            .
          </Heading>
          <Button size="lg" className="mt-40 w-full md:w-auto" as="a" href={`mailto:${config.get('supportEmail')}`}>
            {t('schedule.cantFindAppointment.contactSupport')}
          </Button>
        </Modal>
        <Modal {...appointmentNotFoundModalProps}>
          <Heading level="h4" className="mb-16">
            {t('schedule.appointmentNotFound.modalHeading')}
          </Heading>
          <Paragraph>
            {t('schedule.appointmentNotFound.modalSubHeading', { supportEmail: config.get('supportEmail') })}
          </Paragraph>
          <Button size="lg" className="mt-40" as="a" href={`mailto:${config.get('supportEmail')}`}>
            {t('schedule.appointmentNotFound.confirmButton')}
          </Button>
        </Modal>
      </Container>
    </>
  );
};

export default ScheduleUI;
